/* eslint-disable react-hooks/exhaustive-deps */

import { ConfirmationModal } from "@data-display/ConfirmationModal";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Button, Grid, ListItem, Slide, Typography } from "@mui/material";
import { createRef, useCallback, useEffect, useReducer } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MasterListItemDefault from "./components/MasterListItemDefault";
import { ListCount, ListHeader, ListOfItems, LowerCase, MasterDetailWrapper, classes } from "./styles";

/**
 * @param {Object} props
 * @param {Object} props.labels labels : {singular,plural} Required
 * @param {String} props.textButtonAdd text for button add ( if not defined, no button add)
 * @param {Number} props.step step of skip item in hookSearch  (optionnal) default = 10
 * @param {Function} props.MasterListItem component item to display (optionnal)
 * @param {Boolean} props.hasAvatar if component item has avatar (optionnal)
 * @param {Boolean} props.canDelete if item can be delete
 * @param {Boolean} props.canAdd if item can be add
 * @param {Boolean} props.canUpdate if item can be update
 * @param {Object} props.masterWrapping wrapping master to display item default required : {title,subTitle,description}
 * @param {Function} props.ContraintSearch Component with filter ( search , filter, etc...)
 * @param {Number} props.headerHeight height of the header part
 * @param {Function} props.hookSearch hook ( callback who return object with ({data:Array,total:Number}), ex call api)
 * @param {Function} props.DetailPage Component to display detail page
 * @param {Function} props.onRemoveItem Callback when trash button is clicked on item
 * @param {Function} props.onUpdateItem Callback when save button is clicked on DetailPage
 * @param {Function} props.onCreateItem Callback when create button is clicked on DetailPage
 * @param {Object} props.receiveResult hookResult receive after onRemove, onCreateItem, or onUpdateItem {type : "delete" or "create" or "update", data : some data}

 */
export const MasterContainer = (props) => {
  const {
    id,
    labels,
    textButtonAdd,
    step,
    hasAvatar,
    canAdd,
    canUpdate,
    masterWrapping,
    MasterListItem,
    ContraintSearch,
    headerHeight,
    hookSearch,
    onUpdateItem,
    onCreateItem,
    receiveResult,
    DetailPage,
    deleteOptions,
    ...rest
  } = props;
  const { onRemoveItem, canDelete, modalDefinition, highlightRemovedItem } = deleteOptions || {};

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  let selectedItemId;

  if (params.id) {
    selectedItemId = params.id;
  }

  const stepScrolling = step || 10;
  const [state, setState] = useReducer((state, newState) => ({ ...state, ...newState }), {
    masterList: [],
    total: 0,
    hasNextPage: false,
    isLoading: false,
    filter: {
      sort: JSON.stringify({ createdAt: -1 }),
      selectedId: selectedItemId,
    },
    skip: 0,
    isModalOpen: false,
    itemRemoving: false,
    itemIdSelected: selectedItemId,
  });

  const [{ data: hookResult }, getResult] = hookSearch();

  const containerRef = createRef();

  const handleScrollYChange = () => {
    const lastScrollY = containerRef.current && containerRef.current.scrollTop;
    if (lastScrollY) {
      sessionStorage.setItem("scrollListContainer", lastScrollY);
    }
  };

  //--------------------------Use Effect PART---------------------------------------//
  /**
   * @Init component
   */
  useEffect(() => {
    setState({ isLoading: true });
    getResult({ ...state.filter, skip: state.skip, limit: stepScrolling });
    window.addEventListener("scroll", handleScrollYChange, true);
    sessionStorage.setItem("scrollListContainer", 0);

    return () => {
      window.removeEventListener("scroll", handleScrollYChange);
      sessionStorage.removeItem("scrollListContainer");
    };
  }, []);

  /**
   * @APIhook call retrieving data to fill Master's items' list.
   * @display selected item details if there is a defined selected index.
   * @handle pagination.
   * @example if this master-detail component handle Workshops the APIhook
   * call our backend API to retrieve associated datas.
   */
  useEffect(() => {
    if (hookResult) {
      const updatedState = {
        hasNextPage: state.masterList.length + hookResult.data?.length < hookResult.total,
        skip: state.skip + stepScrolling,
        total: hookResult.total,
      };
      if (state.hasNextPage) {
        updatedState.masterList = [...state.masterList, ...hookResult.data];
      } else {
        updatedState.masterList = [].concat(...hookResult.data);
      }
      updatedState.isLoading = false;
      setState(updatedState);
    }
  }, [hookResult]);

  /**
   * @watch for item CRUD actions
   * @update master's item list depending of the action.
   *  @Change detail's data when index change.
   */
  useEffect(() => {
    const scrollPosition = Number.parseInt(sessionStorage.getItem("scrollListContainer"), 10);
    if (!isNaN(scrollPosition) && scrollPosition > 0) {
      setTimeout(() => containerRef.current.scrollTo(0, scrollPosition), 0);
    }
  }, [selectedItemId]);

  useEffect(() => {
    if (receiveResult && receiveResult.type) {
      const { data, type } = receiveResult;
      switch (type) {
        case "archive":
          if (data._id) {
            setState({
              masterList: state.masterList.map((item) => (item._id === data._id ? data : item)),
            });
          }
          break;
        case "delete":
          // set skip and total -1, otherwise we can loose one item in the next getesult
          state.masterList.splice(state.itemRemoving.index, 1);
          const updatedState = {
            total: state.total - 1,
            skip: state.skip ? state.skip - 1 : state.skip,
            masterList: state.masterList,
            itemRemoving: null,
          };
          setState(updatedState);
          break;
        case "create":
          if (data._id) {
            onClickItem(data, 0);
            state.masterList[0] = data;
            setState({
              masterList: state.masterList,
              skip: state.skip + 1,
            });
          }
          break;
        case "update":
          if (data._id) {
            state.masterList[selectedIndex] = data;
            setState({
              masterList: state.masterList,
            });
          }
          break;
        default:
          // not or bad hookResult
          break;
      }
    }
  }, [receiveResult]);

  //--------------------------INFINITE SCROLL PART---------------------------------------//
  // infinite scrolling function part
  const handleLoadMore = () => {
    setState({ isLoading: true });
    // Some API call to fetch the next page ( skip est calculate on use effect of getResult)
    getResult({ ...state.filter, skip: state.skip, limit: stepScrolling });
  };

  //--------------------------onClick/confirm function PART---------------------------------------//

  // ----------- select part ----------- //
  /**
   * @navigate - push id on url and let some time to reload and set selected index
   * @param {*} item
   * @param {*} index
   */
  const onClickItem = (item, index) => {
    /* comment until found solution for scroll to item selected */
    if (navigate && item._id) {
      const pathToNavigate = params.id
        ? `${location.pathname.substring(0, location.pathname.lastIndexOf("/"))}/${item._id}`
        : `${location.pathname}/${item._id}`;
      navigate(pathToNavigate, {
        state: { selectedIndex: index },
        replace: true,
      });
    } else if (navigate && isOnCreate()) {
      const pathToNavigate = params.id
        ? `${location.pathname.substring(0, location.pathname.lastIndexOf("/"))}`
        : `${location.pathname}`;
      navigate(pathToNavigate, {
        state: { selectedIndex: 0 },
        replace: true,
      });
    }
  };

  // ----------- add part ----------- //
  /**
   * @navigate - push url with / and push on top of the list a new item
   */
  const onClickAdd = () => {
    if (navigate) {
      const pathToNavigate = params.id
        ? `${location.pathname.substring(0, location.pathname.lastIndexOf("/"))}`
        : `${location.pathname}`;
      navigate(pathToNavigate, { replace: true });
    }

    setTimeout(
      () =>
        containerRef && containerRef.current && containerRef.current.scrollTo && containerRef.current.scrollTo(0, 0),
      0
    );
    // empty item is put first in the list
    setState({
      masterList: [{}, ...state.masterList],
      total: state.total + 1,
    });
  };

  // ----------- create/update part ----------- //
  /**
   * @update
   * @create
   * @param {*} item
   * @param {*} index
   */
  const onClickSaveButton = useCallback((item, index) => {
    if (Object.keys(item).length !== 0 && item) {
      if (onUpdateItem && item._id) {
        onUpdateItem(item);
      } else if (onCreateItem) {
        onCreateItem(item);
      }
    }
  }, []);

  // ----------- delete part ----------- //
  /**
   * @click - on click remove/archive button, open modal confirmation
   * @param {*} item
   * @param {*} index
   */
  const onClickTrashButton = useCallback((item, index) => {
    setState({
      itemRemoving: { item, index },
      isModalOpen: true,
    });
  }, []);

  const onConfirmRemoveItem = () => {
    const updatedState = {};
    if (onRemoveItem && canDelete) {
      if (state.itemRemoving && state.itemRemoving.item && state.itemRemoving.item._id) {
        onRemoveItem(state.itemRemoving.item, state.itemRemoving.index);
      } else {
        // remove no create item
        updatedState.total = state.total - 1;
        updatedState.skip = state.skip ? state.skip - 1 : state.skip;
        state.masterList.splice(state.itemRemoving.index, 1);
        updatedState.masterList = state.masterList;
        updatedState.itemRemoving = null;
      }
    }
    updatedState.isModalOpen = false;
    setState(updatedState);
  };

  /**
   * @cancel - on cancel modal
   */
  const onCancelRemoveItem = () => {
    setState({
      itemRemoving: null,
      isModalOpen: false,
    });
  };

  // ----------- other part ----------- //
  /**
   * @return {Boolean} - return if is on creation
   */
  const isOnCreate = () => {
    if (state.masterList && state.masterList[0]) {
      return !state.masterList[0]._id;
    }
    return false;
  };

  /**
   * @Reload - reload the list
   * @param {Object} newFilter
   */
  const onReload = (newFilter = state.filter) => {
    delete newFilter.selectedId;

    setState({
      masterList: [],
      isLoading: false,
      skip: 0,
      total: 0,
      filter: newFilter,
    });
    /* ça on blur le champs recherche*/
    // if (navigate) {
    //   navigate(`${baseUrl}`);
    // }
    getResult({ ...newFilter, skip: 0, limit: stepScrolling });
  };

  /**
   * @filter - put new filter and start reload
   * @param {Object} newfilter
   */
  const onSetFilter = (newfilter) => {
    if (newfilter) {
      onReload(newfilter);
    }
  };

  const callHighlightRemovedItem = (item) => {
    if (highlightRemovedItem && typeof highlightRemovedItem === "function") {
      return highlightRemovedItem(item);
    }

    return false;
  };

  let selectedIndex = state.masterList.findIndex((d) => d._id === selectedItemId);
  selectedIndex = selectedIndex === -1 && isOnCreate() ? 0 : selectedIndex;

  return (
    <MasterDetailWrapper>
      {/* master Side */}
      <Grid
        container
        className={classes.list}
        size={{
          md: 5
        }}>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "center",
            justifyContent: "flex-start",
          }}
          size={9}>
          <ListHeader item xs={12} container headerHeight={headerHeight}>
            {/* if search part in props */}
            {ContraintSearch && (
              <Grid size={12}>
                <ContraintSearch id={"search-input-id"} filter={state.filter} setFilter={onSetFilter}></ContraintSearch>
              </Grid>
            )}
            {!textButtonAdd || !canAdd ? null : (
              <Grid align={"right"} size={12}>
                <Button
                  id="create-button-masterdetail"
                  size="small"
                  onClick={() => onClickAdd()}
                  disabled={state.isLoading || isOnCreate()}
                  aria-label={textButtonAdd}
                  role="button"
                  variant="contained"
                  color="primary"
                  startIcon={<AddRoundedIcon />}
                >
                  {textButtonAdd}
                </Button>
              </Grid>
            )}
          </ListHeader>
          <ListCount item xs={12}>
            {state.masterList && state.masterList.length > 0 && state.total && (
              <Typography variant="caption" color="textSecondary">
                {labels.plural} : {state.masterList.length} / {state.total}
              </Typography>
            )}
          </ListCount>
          {/* master list container overflow who accepted the infinite list*/}
          <ListOfItems
            id={`scrollListViewContainer-${id}`}
            ref={containerRef}
            hasSearch={!!ContraintSearch}
            headerHeight={headerHeight}
            item
            xs={12}
          >
            {/* list with infinite scrolling */}
            <InfiniteScroll
              dataLength={state?.masterList?.length}
              next={handleLoadMore}
              hasMore={state.hasNextPage}
              scrollableTarget={`scrollListViewContainer-${id}`}
            >
              {/* No result part */}
              {!state.isLoading && state.masterList && state.masterList.length === 0 && (
                <ListItem key={-2}>
                  <Grid
                    container
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Grid
                      container
                      direction="column"
                      sx={{
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                      }}
                      size={12}>
                      <Typography variant="body2" noWrap gutterBottom={false} component={LowerCase}>
                        Aucun {labels.singular} ne correspond à votre recherche.
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              )}
              {/* list of items */}
              {state.masterList &&
                state.masterList.length > 0 &&
                state.masterList.map((masterItem, index) => {
                  if (MasterListItem) {
                    return (
                      <MasterListItem
                        key={`MasterListItem-${index}`}
                        role="menuitem"
                        index={index}
                        item={masterItem}
                        isDeletable={masterItem.isDeletable || canDelete || false}
                        selected={selectedIndex === index}
                        masterWrapping={masterWrapping}
                        isSkeleton={false}
                        hasAvatar={hasAvatar}
                        onClick={() => onClickItem(masterItem, index)}
                        onRemove={() => onClickTrashButton(masterItem, index)}
                      />
                    );
                  } else {
                    return (
                      <MasterListItemDefault
                        key={`MasterListItemDefault-${index}`}
                        role="menuitem"
                        index={index}
                        item={masterItem}
                        isDeletable={masterItem.isDeletable || canDelete || false}
                        selected={selectedIndex === index}
                        masterWrapping={masterWrapping}
                        isSkeleton={false}
                        hasAvatar={hasAvatar}
                        onClick={() => onClickItem(masterItem, index)}
                        onRemove={() => onClickTrashButton(masterItem, index)}
                        isArchived={callHighlightRemovedItem(masterItem) || false}
                      />
                    );
                  }
                })}
              {/* loading skeleton item on bottom of the list */}
              {!state.isLoading ? null : MasterListItem ? (
                <MasterListItem
                  key={`MasterListItem-${-1}`}
                  item={{}}
                  index={-1}
                  isSkeleton={true}
                  hasAvatar={hasAvatar}
                  masterWrapping={masterWrapping}
                />
              ) : (
                <MasterListItemDefault
                  key={`MasterListItemDefault-${-1}`}
                  item={{}}
                  index={-1}
                  isSkeleton={true}
                  hasAvatar
                  masterWrapping={masterWrapping}
                />
              )}
            </InfiniteScroll>
          </ListOfItems>
        </Grid>
      </Grid>
      {/* Detail Side */}
      {selectedIndex > -1 && (
        <Slide direction="left" in={!!state.masterList[selectedIndex]} exit={true} timeout={150}>
          <Grid
            container
            className={classes.detail}
            size={{
              md: 7
            }}>
            <Grid
              container
              sx={{
                justifyContent: "flex-start",
              }}
              size={10}>
              {state.masterList[selectedIndex] && (
                <DetailPage
                  id={`detail-${new Date().valueOf()}`}
                  key={`detail-${new Date().valueOf()}`}
                  value={state.masterList[selectedIndex]}
                  hasSearchBar={!!ContraintSearch}
                  headerHeight={headerHeight}
                  canUpdate={canUpdate}
                  canAdd={canAdd}
                  onCreate={(item) => onClickSaveButton(item, selectedIndex)}
                  onUpdate={(item) => onClickSaveButton(item, selectedIndex)}
                />
              )}
            </Grid>
          </Grid>
        </Slide>
      )}
      {state.isModalOpen && (
        <ConfirmationModal
          {...modalDefinition}
          open={state.isModalOpen}
          onConfirmClick={() => onConfirmRemoveItem()}
          onCancelClick={() => onCancelRemoveItem()}
        >
          {modalDefinition?.body}
        </ConfirmationModal>
      )}
    </MasterDetailWrapper>
  );
};
